/**
 * Whether a presentation's state-changing steps may be delivered as one run.
 *
 * The webview is the LAST of three gates a batch passes through, and for a day
 * it was the one nobody knew about: the MCP contract was relaxed to accept a
 * run of clicks and the Rust delivery gate was taught to let non-final actions
 * through, but `normalizeAgentPresentation` still answered `null` for any
 * sequence with more than one operating step. A batch was built, accepted,
 * broadcast - and dropped here without being drawn. Batching had never once
 * executed.
 *
 * So this file exists to make the three gates agree, and to make the
 * disagreement visible if they ever drift again.
 *
 * # This gate checks shape, not eligibility
 *
 * It deliberately does NOT re-derive whether each step is a semantic click.
 * By the time a presentation reaches the webview its targets have been resolved
 * to screen geometry, so "was this a semantic_target?" is no longer answerable
 * here - and guessing at it would be a third, weaker opinion about a question
 * two other gates already answer from better evidence:
 *
 * - `desktop-interact-batch.ts` (MCP) refuses anything but a semantic click,
 *   refuses mixed batches, and caps the count.
 * - `presentation_delivery/gate.rs` (Rust, downstream of this file) refuses to
 *   deliver any non-final action that is not `press_ui_element`.
 *
 * The second one is what makes this safe: a batch that should never have been
 * built still cannot act, because delivery refuses it after we render it.
 *
 * # Why the platform predicate is repeated rather than imported
 *
 * `platformBatchesActions()` in the MCP server reads `process.platform` in a
 * Node process. This runs in a webview. They cannot share a module, so they
 * share a rule instead, and each says where the other one lives.
 */
import { isWindowsPlatform } from "./os-platform";

/**
 * Mirror of `MAX_BATCHED_ACTIONS` in
 * `mcp-servers/canvas/src/desktop-interact-batch.ts`, which is the source of
 * truth and carries the reasoning for the number.
 *
 * Duplicated rather than imported because that file is bundled into an MCP
 * server and this one into the webview. Raising it there without raising it
 * here would not loosen anything - it would make the extra steps vanish
 * silently, which is the failure this whole file exists to prevent. A drift
 * test in `agent-presentation-batch.test.ts` pins them together.
 */
export const MAX_DELIVERABLE_ACTIONS = 3;

/** Steps that change the screen rather than describe it. */
const OPERATING_STEP_KINDS = [
  "click",
  "hover",
  "drag",
  "scroll",
  "paste",
  "key",
];

export function isOperatingStepKind(kind: string): boolean {
  return OPERATING_STEP_KINDS.includes(kind);
}

/**
 * Whether this platform may render a run of actions at all.
 *
 * Windows only, matching `platformBatchesActions()` in the MCP server and
 * `BATCHES_ACTIONS` in `presentation_delivery/gate.rs`. macOS keeps exactly the
 * rule it has always had - one action, and it must be last - until someone can
 * build and test the Mac app. Flipping this is a predicate, not a port; what
 * has to be verified first is written down in
 * `docs/plans/2026-09-16-macos-computer-use-catch-up.md`.
 */
export function platformDeliversBatchedActions(): boolean {
  return isWindowsPlatform();
}

/**
 * Whether these operating steps may be presented.
 *
 * `guide` mode never operates anything - that rule is absolute and platform
 * independent. Beyond it, a single action must be last (unchanged, every
 * platform), and a run of actions must be the unbroken tail of the sequence:
 * narration and marks first, then the actions. A describing step in the middle
 * would be rendered between two actions whose targets were both resolved before
 * either ran, which reads as commentary on a screen that has already moved.
 */
export function operatingStepsMayBePresented(
  mode: "operate" | "guide",
  operatingIndexes: number[],
  stepCount: number,
): boolean {
  if (mode === "guide") return operatingIndexes.length === 0;
  if (operatingIndexes.length === 0) return true;
  const lastIndex = stepCount - 1;
  if (operatingIndexes.length === 1) return operatingIndexes[0] === lastIndex;
  if (!platformDeliversBatchedActions()) return false;
  if (operatingIndexes.length > MAX_DELIVERABLE_ACTIONS) return false;
  const expectedFirst = lastIndex - operatingIndexes.length + 1;
  return operatingIndexes.every(
    (index, position) => index === expectedFirst + position,
  );
}
